import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, ArrowLeft, FileDown } from "lucide-react";
import jsPDF from "jspdf";
import api from "../lib/api";

const fmt = (n, cur) => (cur === "usd" ? "$" : "Rp") + Number(n || 0).toLocaleString("id-ID");

export const STATUS_BADGE = {
  paid: { label: "Berhasil", cls: "bg-su-success/20 text-su-success" },
  pending: { label: "Menunggu", cls: "bg-su-energy/20 text-su-energy" },
  failed: { label: "Gagal", cls: "bg-su-danger/20 text-su-danger" },
};

export function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso || "-"; }
}

export function generateInvoicePDF(t) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const money = fmt(t.amount, t.currency);
  // header bar
  doc.setFillColor(10, 15, 30);
  doc.rect(0, 0, W, 90, "F");
  doc.setTextColor(0, 229, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text("SkorUp.io", 40, 45);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text("STRUK PEMBAYARAN / INVOICE", 40, 68);
  // meta
  doc.setTextColor(40, 40, 40);
  doc.setFontSize(10);
  let y = 130;
  const meta = [
    ["No. Invoice", t.order_id],
    ["Tanggal", fmtDate(t.created_at)],
    ["Metode Pembayaran", `${t.gateway}${t.method && t.method !== "-" ? " · " + t.method : ""}`],
    ["Status", t.status === "paid" ? "LUNAS" : (t.status === "pending" ? "MENUNGGU" : "GAGAL")],
  ];
  meta.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold"); doc.text(String(k), 40, y);
    doc.setFont("helvetica", "normal"); doc.text(String(v || "-"), 200, y);
    y += 22;
  });
  // table
  y += 14;
  doc.setFillColor(240, 244, 250); doc.rect(40, y - 14, W - 80, 26, "F");
  doc.setFont("helvetica", "bold");
  doc.text("Deskripsi", 50, y + 3); doc.text("Jumlah", W - 120, y + 3);
  y += 34;
  doc.setFont("helvetica", "normal");
  doc.text(String(t.product_name || "-"), 50, y);
  doc.text(money, W - 120, y);
  y += 20;
  doc.setDrawColor(210); doc.line(40, y, W - 40, y);
  y += 26;
  doc.setFont("helvetica", "bold"); doc.setFontSize(13);
  doc.text("TOTAL", 50, y);
  doc.setTextColor(0, 150, 170);
  doc.text(money, W - 120, y);
  // footer
  doc.setTextColor(130, 130, 130); doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text("Terima kasih atas pembelianmu di SkorUp.io. Simpan struk ini sebagai bukti pembayaran.", 40, 760);
  doc.text("Dokumen ini sah tanpa tanda tangan.", 40, 774);
  doc.save(`invoice-skorup-${t.order_id}.pdf`);
}

export default function Transactions() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/payments/my-transactions").then(({ data }) => setRows(data.transactions)).catch(() => setRows([]));
  }, []);

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="transactions-page">
      <div className="flex items-center gap-3">
        <Link to="/pricing" className="text-su-muted hover:text-su-text"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex items-center gap-2">
          <Receipt className="w-6 h-6 text-su-primary" />
          <h1 className="font-heading font-black text-2xl">Riwayat Transaksi</h1>
        </div>
      </div>
      <p className="text-su-muted font-medium text-sm">Semua pembelian VIP & token kamu tercatat di sini.</p>

      {rows === null ? (
        <div className="min-h-[30vh] grid place-items-center"><div className="w-10 h-10 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="card-chunky p-8 text-center" data-testid="transactions-empty">
          <p className="text-su-muted font-medium mb-4">Belum ada transaksi.</p>
          <Link to="/pricing" className="text-su-primary font-heading font-extrabold hover:underline">Lihat paket VIP & Token →</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((t) => {
            const b = STATUS_BADGE[t.status] || STATUS_BADGE.pending;
            return (
              <div key={t.order_id} className="card-chunky p-4 flex items-center justify-between gap-3" data-testid={`txn-${t.order_id}`}>
                <div className="min-w-0">
                  <p className="font-heading font-black truncate">{t.product_name}</p>
                  <p className="text-xs text-su-muted font-medium">{fmtDate(t.created_at)} · {t.gateway}</p>
                </div>
                <div className="text-right shrink-0 flex items-center gap-3">
                  <div>
                    <p className="font-heading font-black">{fmt(t.amount, t.currency)}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-lg text-xs font-heading font-black ${b.cls}`}>{b.label}</span>
                  </div>
                  {t.status === "paid" && (
                    <button data-testid={`txn-pdf-${t.order_id}`} onClick={() => generateInvoicePDF(t)} title="Unduh Struk PDF"
                      className="w-10 h-10 grid place-items-center rounded-xl border-2 border-su-border bg-su-surface text-su-primary hover:border-su-primary transition-colors shrink-0">
                      <FileDown className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
